import {useState} from 'react';
import {useMutation} from '@apollo/client';
import {toast} from 'react-toastify';
import {PRE_SIGN_MEDIA_URL} from 'graphql/mutations/wallet';
import {PRESIGN_EXPIRY_SECONDS} from './kybConstants';
import {parseS3Url, unwrap} from './kybUtils';

/**
 * Opens an uploaded KYB document.
 *
 * `fileUrl` is a raw object URL on a private bucket — putting it in an `href`
 * gives the reviewer a 403 and no explanation. So the link is presigned on
 * click and opened straight away: signed URLs never enter page state, and a
 * link that was rendered ten minutes ago cannot have gone stale by the time
 * it is used.
 *
 * `openingId` lets a row show its own spinner rather than every row spinning
 * together.
 */
export const useKybDocumentPreview = () => {
  const [presignMediaURL] = useMutation(PRE_SIGN_MEDIA_URL);
  const [openingId, setOpeningId] = useState(null);

  const openDocument = async document => {
    const target = parseS3Url(document?.fileUrl);
    if (!target) {
      toast.error('This file has no usable storage link.');
      return;
    }

    setOpeningId(document?.id || target.filename);
    try {
      const {data} = await presignMediaURL({
        variables: {
          input: {
            bucket: target.bucket,
            filename: target.filename,
            upload: false,
            expiry: PRESIGN_EXPIRY_SECONDS,
          },
        },
      });

      const {data: payload, error} = unwrap(data?.presignMediaURL);
      if (error) {
        toast.error(error);
        return;
      }

      const url = payload?.url;
      if (!url) {
        toast.error('Could not generate a link for this file.');
        return;
      }

      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (error) {
      toast.error('Could not open this file.');
    } finally {
      setOpeningId(null);
    }
  };

  /** Whether a document can be opened at all — drives the disabled state. */
  const canOpen = document => Boolean(parseS3Url(document?.fileUrl));

  return {openDocument, canOpen, openingId};
};
